/**
 * Per-drop generation helpers for multi-country AI orchestration.
 * Maps global drop indices to per-country local indices and drives
 * the per-drop API loop with progress reporting.
 */

/** Parallel cap — balances speed vs API load (single-country is one call; here we fan out per drop). */
const PER_DROP_GENERATION_CONCURRENCY = 4;

export interface GenerationProgress {
  /** Completed requests (0 … total). */
  current: number;
  total: number;
  currentCountry: string;
}

export interface GlobalDropEntry {
  countryKey: string;
  localIdx: number;
}

/** One entry per affected drop in merge order (selected + propagation), aligned with groupIdsPerDrop / dropLabels. */
export function buildGlobalCountrySequence(
  orderedDropIds: string[],
  drops: Array<{ id: string; countryKey: string }>,
): string[] {
  const byId = new Map(drops.map((d) => [d.id, d]));
  const seq: string[] = [];
  for (const id of orderedDropIds) {
    const d = byId.get(id);
    if (d) seq.push(d.countryKey);
  }
  return seq;
}

export function buildGlobalToLocalMap(
  countryKeys: string[],
  dropCountByCountry: Map<string, number>,
): GlobalDropEntry[] {
  const result: GlobalDropEntry[] = [];
  for (const ck of countryKeys) {
    const count = dropCountByCountry.get(ck) ?? 0;
    for (let i = 0; i < count; i++) result.push({ countryKey: ck, localIdx: i });
  }
  return result;
}

export function inferAmountFromCallback(items: unknown): number {
  if (!Array.isArray(items) || items.length === 0) return 15;
  if (Array.isArray(items[0])) return (items[0] as unknown[]).length || 15;
  return items.length;
}

async function runPool(count: number, limit: number, worker: (index: number) => Promise<void>): Promise<void> {
  let next = 0;
  const runWorker = async (): Promise<void> => {
    for (;;) {
      const i = next;
      next += 1;
      if (i >= count) return;
      await worker(i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, Math.max(count, 1)) }, () => runWorker()));
}

export interface GeneratePerDropParams<T> {
  countryKeys: string[];
  /** Country per global drop index (merge order); must match groupIdsPerDrop / AI picker rows. */
  globalCountryPerDrop: string[];
  groupId: string;
  amount: number;
  groupIdsPerDrop?: string[];
  translate?: boolean;
  genFn: (groupId: string, countryKey: string, amount: number, translate?: boolean) => Promise<T>;
  extractResult: (data: T) => string[];
  extractTranslations?: (data: T) => string[] | undefined;
  setProgress: (p: GenerationProgress) => void;
  applyResult: (
    countryKey: string,
    batches: string[][],
    groupIds: string[],
    translationBatches: string[][],
  ) => void;
  /** Batches in global merge order (same index as `globalCountryPerDrop`). */
  onMergeOrderBatches?: (batches: string[][]) => void;
}

export async function generatePerDrop<T>(p: GeneratePerDropParams<T>): Promise<void> {
  const mapping = p.globalCountryPerDrop;
  const total = mapping.length;
  p.setProgress({ current: 0, total, currentCountry: mapping[0] ?? '' });

  const slotResults: Array<{
    countryKey: string;
    batch: string[];
    translations: string[] | undefined;
    gId: string;
  } | undefined> = [];
  let completed = 0;

  await runPool(total, PER_DROP_GENERATION_CONCURRENCY, async (gi) => {
    const countryKey = mapping[gi]!;
    const gId = p.groupIdsPerDrop?.[gi] ?? p.groupId;
    p.setProgress({ current: completed, total, currentCountry: countryKey });
    const data = await p.genFn(gId, countryKey, p.amount, p.translate);
    const translations = p.extractTranslations?.(data);
    slotResults[gi] = { countryKey, batch: p.extractResult(data), translations, gId };
    completed += 1;
    p.setProgress({ current: completed, total, currentCountry: countryKey });
  });

  const batchesByCountry = new Map<string, string[][]>();
  const transByCountry = new Map<string, string[][]>();
  const gIdsByCountry = new Map<string, string[]>();
  for (const ck of p.countryKeys) {
    batchesByCountry.set(ck, []);
    transByCountry.set(ck, []);
    gIdsByCountry.set(ck, []);
  }
  for (let gi = 0; gi < total; gi++) {
    const r = slotResults[gi]!;
    batchesByCountry.get(r.countryKey)!.push(r.batch);
    transByCountry.get(r.countryKey)!.push(r.translations ?? []);
    gIdsByCountry.get(r.countryKey)!.push(r.gId);
  }
  p.onMergeOrderBatches?.(slotResults.map((r) => r!.batch));
  for (const ck of p.countryKeys) {
    p.applyResult(ck, batchesByCountry.get(ck)!, gIdsByCountry.get(ck)!, transByCountry.get(ck)!);
  }
  p.setProgress({ current: total, total, currentCountry: '' });
}
